import { z } from "zod";
import { IsoDateSchema, optionalQuery } from "./primitives.js";
/**
 * Reading `audit_log` (spec §13, §14.3) — the contract behind `audit.view`.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE TABLE HAS BEEN WRITE-ONLY SINCE THE DAY IT WAS CREATED.
 *
 * `audit_log` has been written by every mutating path in this API from the
 * beginning; §2.1 adds every DENIED attempt on top of that, and login auditing
 * landed on 2026-08-17 — sign-ins, sign-outs, refusals, lockouts. It is retained
 * for 72 months because under GST the books must be reconstructable (§14.3).
 * Nothing could read a single row of it. The owner asked for the other half in
 * as many words: "a full audit window for me — who logged in when, when was what
 * happened, and this will stay indefinitely."
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * `audit.view` IS ADMIN-ONLY AND MUST STAY SO. §2.1 lists it on the ADMIN row
 * explicitly, which is why it is NOT in the shared `*.view` block in
 * permissions.ts even though it is textually a `*.view` capability — the more
 * specific row wins. The audit trail is an administration surface, not a
 * document view: it contains every refused attempt, every lockout, every
 * sign-in, and the before-image of every settings change.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY FILTER IS A REAL COLUMN. NOTHING QUERIES INSIDE THE JSON.
 *
 * `before_json` and `after_json` are TEXT-BACKED JSON columns; CLAUDE.md is
 * blunt about it — "write and read them whole, never query inside them". MariaDB
 * would happily accept `JSON_EXTRACT(after_json, '$.role') = 'ADMIN'` and would
 * read every row of a six-year table to answer it, on a small VPS, with no index
 * that could ever help. So the four filters below are `actor_user_id`,
 * `entity_type` + `entity_id`, `action` and `created_at`, and each one is backed
 * by an index (see `prisma/sql/16_audit_indexes.sql`).
 * ─────────────────────────────────────────────────────────────────────────────
 */
/* -------------------------------------------------------------------------- */
/* 1. Paging                                                                   */
/* -------------------------------------------------------------------------- */
/** A page nobody has to scroll past, and the size the ADMIN screen opens on. */
export const AUDIT_PAGE_DEFAULT = 50;
/**
 * The hard ceiling on one page.
 *
 * ON THE BIGGEST TABLE IN THE DATABASE, RETAINED FOR SIX YEARS, this is not
 * politeness. Every row carries two JSON blobs — a settings change holds the
 * whole company row twice — so an uncapped `?limit=100000` is a multi-megabyte
 * response assembled in the memory of a small VPS that is also serving the
 * father's phone. The cap is enforced by the schema, so it is a 400 rather than
 * something a handler has to remember to clamp.
 */
export const AUDIT_PAGE_MAX = 100;
/**
 * How deep offset paging is allowed to go before the caller is told to filter.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CAP ON `offset` IS UNUSUAL, AND THIS TABLE IS THE REASON FOR IT. `OFFSET n`
 * is not free: MariaDB walks and discards `n` index entries before it returns
 * anything, so page 400 of an unfiltered six-year log is a scan whose cost grows
 * with how bored the reader is. Every other list in this API is over a table
 * with hundreds of rows and none of them needs this.
 *
 * The refusal is not "no": it points at the date range, which IS indexed
 * (`created_at`) and which is how somebody actually looks for "what happened
 * last March". Nothing legitimate needs to walk ten thousand rows one page at a
 * time to get there.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const AUDIT_OFFSET_MAX = 10_000;
/**
 * The longest `entity_type` / `action` the columns hold — both `VarChar(64)`.
 *
 * Stated once and used by the schema below, for the reason `primitives.ts` gives
 * for `INDEXED_STRING_MAX`: a zod rule looser than its column is a silent trap.
 * Here it would only ever produce an empty result rather than a truncation — a
 * 65-character action matches nothing — but "the filter quietly found nothing"
 * is a worse answer on an audit screen than a 400 naming the field.
 */
export const AUDIT_STRING_MAX = 64;
/* -------------------------------------------------------------------------- */
/* 2. The query                                                                */
/* -------------------------------------------------------------------------- */
/**
 * `GET /audit?actorUserId=&entityType=&entityId=&action=&from=&to=&limit=&offset=`.
 *
 * `.strict()`, so an unknown key is a 400 rather than a filter that appears to
 * work — which on THIS screen would mean an ADMIN concluding that nothing
 * happened when in fact nothing was filtered.
 *
 * The defaults sit INSIDE `optionalQuery` for the reason spelled out in
 * `messages.ts` and `users.ts`: the SPA sends every key whether or not it is
 * set, and a `.default()` applied outside the preprocess never fires on `?limit=`.
 *
 * `entityId` REQUIRES `entityType`, and the refinement below enforces it. An id
 * on its own is not a filter — row 5 exists in every table in this database, so
 * "entity 5" would return an invoice, a customer and a user side by side — and
 * it is not indexable either: the composite index is `(entity_type, entity_id,
 * created_at)`, whose leftmost column is the type. Answering that request would
 * be a full scan of a 72-month table producing a list nobody asked for.
 */
export const AuditQuerySchema = z
    .object({
    /** Everything one person did. Indexed by `(actor_user_id, created_at)`. */
    actorUserId: optionalQuery(z.coerce
        .number({ invalid_type_error: "actorUserId must be a number" })
        .int("actorUserId must be a whole number")
        .positive("actorUserId must be positive")
        .optional()),
    /**
     * `invoice`, `customer`, `user`, `capability`, … — the `entity_type` column
     * verbatim. Deliberately NOT an enum: `audit_log.action` and `entity_type`
     * are `VarChar(64)` rather than enums precisely because the catalogue grows
     * every time the app learns to record something, and a union here would have
     * to be edited in lockstep with every future write path or the screen would
     * refuse to filter on a value the table already contains.
     */
    entityType: optionalQuery(z
        .string()
        .trim()
        .max(AUDIT_STRING_MAX, `entityType must be ${AUDIT_STRING_MAX} characters or fewer`)
        .optional()),
    /** One document's whole history. Only meaningful beside `entityType`. */
    entityId: optionalQuery(z.coerce
        .number({ invalid_type_error: "entityId must be a number" })
        .int("entityId must be a whole number")
        .positive("entityId must be positive")
        .optional()),
    /** `invoice.cancel`, `auth.denied`, `user.role_changed`, … */
    action: optionalQuery(z
        .string()
        .trim()
        .max(AUDIT_STRING_MAX, `action must be ${AUDIT_STRING_MAX} characters or fewer`)
        .optional()),
    /**
     * Both ends INCLUSIVE calendar days, `YYYY-MM-DD`, resolved in Asia/Kolkata
     * — never the machine's zone (CLAUDE.md). `to=2026-08-31` includes
     * everything that happened up to 23:59:59.999 IST on the 31st; the exclusive
     * upper bound the query actually uses is an implementation detail that stays
     * on the server, exactly as `ReportPeriod` already documents for reports.
     */
    from: optionalQuery(IsoDateSchema.optional()),
    to: optionalQuery(IsoDateSchema.optional()),
    limit: optionalQuery(z.coerce.number().int().min(1).max(AUDIT_PAGE_MAX).default(AUDIT_PAGE_DEFAULT)),
    offset: optionalQuery(z.coerce
        .number()
        .int()
        .min(0)
        .max(AUDIT_OFFSET_MAX, `Paging past ${AUDIT_OFFSET_MAX} rows is refused — narrow the range with from/to instead.`)
        .default(0)),
})
    .strict()
    .refine((query) => query.entityId === undefined || query.entityType !== undefined, {
    message: "entityId needs an entityType beside it — id 5 exists in every table.",
    path: ["entityId"],
});
//# sourceMappingURL=audit.js.map